import React from 'react';
import { AvatarWithFlag } from 'components/AvatarWithFlag';
import ChevronLeftIcon from 'components/Icons/ChevronLeftIcon';
import type { FocusSide } from 'utils/offerFocusState';
import './OfferSideStrip.css';

export interface StripAvatar {
  avatarUrl: string | null;
  username?: string;
  countryCode?: string | null;
  isElite: boolean;
  showAnimatedBorder: boolean;
  borderColor1: string | null;
  borderColor2: string | null;
  isLoading?: boolean;
}

export interface OfferSideStripProps {
  /** The side this strip stands in for while folded. */
  side: FocusSide;
  /** True while this side is folded (the other side is focused). */
  folded: boolean;
  avatar: StripAvatar;
  counterpartyUsername: string;
  /** That side's cart line count. */
  cartCount: number;
  onClick: () => void;
}

/**
 * The folded side of the builder (Asana 1219788320590626 Decision 3): a 56px
 * vertical strip in that side's band gradient. Always mounted; its width and
 * opacity animate with the layout (Decision 13), and while unfolded it is
 * visibility:hidden so it leaves the tab order. The whole strip is one button
 * that returns to four columns.
 */
export const OfferSideStrip: React.FC<OfferSideStripProps> = ({
  side,
  folded,
  avatar,
  counterpartyUsername,
  cartCount,
  onClick,
}) => {
  const yours = side === 'send';
  const label = yours ? 'YOU ADD' : 'THEY ADD';
  const hint = `${counterpartyUsername}'s ${yours ? 'want list' : 'have list'}`;
  return (
    <button
      type="button"
      className={`offer-strip ${yours ? 'offer-strip--yours' : 'offer-strip--theirs'}`}
      onClick={onClick}
      aria-label={`Show both sides (${label.toLowerCase()}: ${hint})`}
      aria-expanded={false}
      aria-hidden={!folded}
      tabIndex={folded ? 0 : -1}
      data-testid={`offer-strip-${side}`}
    >
      <span className={`offer-strip-chevron${yours ? ' offer-strip-chevron--right' : ''}`} aria-hidden="true">
        <ChevronLeftIcon size={14} color="#ffffff" />
      </span>
      <span className="offer-strip-avatar" aria-hidden="true">
        <AvatarWithFlag
          avatarUrl={avatar.avatarUrl}
          username={avatar.username}
          countryCode={avatar.countryCode}
          size={28}
          alt=""
          isLoading={avatar.isLoading}
          isElite={avatar.isElite}
          showAnimatedBorder={avatar.showAnimatedBorder}
          borderColor1={avatar.borderColor1}
          borderColor2={avatar.borderColor2}
        />
      </span>
      <span className="offer-strip-label">{label}</span>
      <span className="offer-strip-count" data-testid={`offer-strip-count-${side}`}>
        {cartCount}
      </span>
      <span className="offer-strip-hint">{hint}</span>
    </button>
  );
};

export default OfferSideStrip;
